import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { reducedMotion } from '../../hooks/useLenis.js';
import { ContactForm } from '../../components/shared/ContactForm/ContactForm.jsx';
import { useMotion } from '../../hooks/useMotion.js';
import './Contact.css';

export const Contact = () => {
  useMotion();
  const contactSectionRef = useRef(null);
  const logoMaskRef = useRef(null);

  useEffect(() => {
    document.title = 'Contact | ASTRID FIRST Wealth Advisory';
  }, []);

  useEffect(() => {
    if (reducedMotion()) return;

    const section = contactSectionRef.current;
    const mask = logoMaskRef.current;
    if (!section || !mask) return;

    const mm = gsap.matchMedia();

    // Desktop and Tablet (>= 768px):
    mm.add('(min-width: 768px)', () => {
      gsap.fromTo(
        mask,
        {
          x: -25,
          opacity: 0.45,
        },
        {
          x: 35,
          opacity: 0.95,
          ease: 'power1.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 80%',
            end: 'bottom 20%',
            scrub: 1.2,
          },
        }
      );
    });

    return () => {
      mm.revert();
    };
  }, []);

  return (
    <div className="contact-page">
      <header className="page-head">
        <div className="page-head-inner">
          <p className="crumb">
            <a href="/">Home</a> / Contact
          </p>
          <h1 data-reveal="chars">Contact</h1>
          <p className="lead" data-reveal="fade">
            Reach out for any inquiries, support, or feedback. We're here to
            help.
          </p>
        </div>
      </header>

      <div className="spine">
        {/* Section 1: Where to find us */}
        <section className="section shell">
          <div className="track">
            <div className="track-head">
              <p className="eyebrow">Where to find us</p>
              <h2 data-reveal="lines">Get in Touch</h2>
            </div>
            <div className="details" data-reveal="group">
              <div className="detail">
                <h3>Our Location</h3>
                <address>
                  <a href="https://maps.app.goo.gl/hy2B7oEYHd48CrvH7">
                    1 Temasek Avenue, #20-01/05, Millenia Tower, Singapore 039192
                  </a>
                </address>
              </div>
              <div className="detail">
                <h3>Phone Number</h3>
                <a href="tel:+6569217700">(+65) 6921 7700</a>
              </div>
              <div className="detail">
                <h3>Email Address</h3>
                <a href="mailto:info@astridfirst.com">
                  info@astridfirst.com
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Full-width Contact Section with skyline background, 50% black overlay, and left logo mask */}
      <section
        ref={contactSectionRef}
        className="section contact-mandate-section stage"
        id="contact-form-section"
        aria-labelledby="contact-form-title"
      >
        <div className="mandate-bg-wrap" aria-hidden="true">
          <img
            src="/media/singapore-by-night-2026-04-14-00-40-27-utc.webp"
            alt="Singapore Skyline at Night"
            className="mandate-bg-img"
          />
          <div className="mandate-overlay" />
          <div className="mandate-right-overlay" />
        </div>

        <div className="shell mandate-shell">
          <div className="mandate-grid">
            {/* Left side: Logo image mask with 100% height of this section */}
            <div className="mandate-visual-col" aria-hidden="true">
              <div className="mandate-logo-sticky">
                <div ref={logoMaskRef} className="mandate-logo-mask" />
              </div>
            </div>

            {/* Right side: Title and contact form */}
            <div className="mandate-content-col">
              <div className="mandate-header">
                <p className="eyebrow mandate-eyebrow">Send a message</p>
                <h2
                  id="contact-form-title"
                  className="mandate-title"
                  data-reveal="lines"
                >
                  Ready to Get Started?
                </h2>
              </div>

              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      <div className="spine">
        {/* Section 3: Office / Marina Bay location */}
        <section className="section shell">
          <div className="frame" data-video-reveal data-scale>
            <div
              className="slot slot--wide"
              data-slot="Map or office image — 16:9"
            >
              <img
                src="/media/marina-bay-area-in-singapore-city-2026-03-10-02-03-07-utc.webp"
                alt="ASTRID FIRST Wealth Advisory Singapore Location - Marina Bay"
                loading="lazy"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};